import './SupportEditor.scss'

import { JSONContent, TextSerializer } from '@tiptap/core'
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight'
import ExtensionDocument from '@tiptap/extension-document'
import { Image } from '@tiptap/extension-image'
import { Link } from '@tiptap/extension-link'
import { Underline } from '@tiptap/extension-underline'
import { Placeholder } from '@tiptap/extensions'
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state'
import {
    EditorContent,
    Extension,
    NodeViewContent,
    NodeViewProps,
    NodeViewWrapper,
    ReactNodeViewRenderer,
} from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { useActions, useValues } from 'kea'
import { common, createLowlight } from 'lowlight'
import posthog from 'posthog-js'
import { useCallback, useEffect, useRef, useState } from 'react'

import { IconCode, IconCopy, IconImage, IconList, IconTerminal } from '@posthog/icons'

import { EmojiPickerPopover } from 'lib/components/EmojiPicker/EmojiPickerPopover'
import { useRichContentEditor } from 'lib/components/RichContentEditor'
import { CommandEnterExtension } from 'lib/components/RichContentEditor/CommandEnterExtension'
import { EmojiSuggestionExtension } from 'lib/components/RichContentEditor/EmojiSuggestionExtension'
import { MentionsExtension } from 'lib/components/RichContentEditor/MentionsExtension'
import { RichContentNodeMention } from 'lib/components/RichContentEditor/RichContentNodeMention'
import { RichContentEditorType, RichContentNodeType, TTEditor } from 'lib/components/RichContentEditor/types'
import { createEditor } from 'lib/components/RichContentEditor/utils'
import { useUploadFiles } from 'lib/hooks/useUploadFiles'
import { IconBold, IconItalic, IconLink } from 'lib/lemon-ui/icons'
import { LemonButton } from 'lib/lemon-ui/LemonButton'
import { LemonFileInput } from 'lib/lemon-ui/LemonFileInput'
import { LemonInput } from 'lib/lemon-ui/LemonInput'
import { emojiUsageLogic } from 'lib/lemon-ui/LemonTextArea/emojiUsageLogic'
import { lemonToast } from 'lib/lemon-ui/LemonToast'
import { Popover } from 'lib/lemon-ui/Popover'
import { Spinner } from 'lib/lemon-ui/Spinner'
import { copyToClipboard } from 'lib/utils/copyToClipboard'
import { cn } from 'lib/utils/css-classes'
import { preflightLogic } from 'scenes/PreflightCheck/preflightLogic'

const lowlight = createLowlight(common)
lowlight.register('plaintext', () => ({ contains: [] }))

function SupportCodeBlockComponent({ node }: NodeViewProps): JSX.Element {
    const code = node.textContent

    return (
        <NodeViewWrapper className="SupportEditor__code-block">
            <div className="SupportEditor__code-block-copy" contentEditable={false}>
                <LemonButton
                    size="xsmall"
                    icon={<IconCopy />}
                    onClick={() => void copyToClipboard(code, 'code')}
                    tooltip="Copy code"
                />
            </div>
            <pre>
                <code>
                    <NodeViewContent />
                </code>
            </pre>
        </NodeViewWrapper>
    )
}

const SupportCodeBlockExtension = CodeBlockLowlight.extend({
    addNodeView() {
        return ReactNodeViewRenderer(SupportCodeBlockComponent)
    },
    addKeyboardShortcuts() {
        return {
            ...this.parent?.(),
            Tab: ({ editor }) => {
                if (editor.isActive('codeBlock')) {
                    editor.commands.insertContent('\t')
                    return true
                }
                return false
            },
            'Shift-Tab': ({ editor }) => {
                if (editor.isActive('codeBlock')) {
                    return true
                }
                return false
            },
        }
    },
}).configure({ lowlight })

type LinkShortcutExtensionOptions = {
    onLinkShortcut: () => void
}

const LinkShortcutExtension = Extension.create<LinkShortcutExtensionOptions>({
    name: 'link-shortcut',

    addOptions() {
        return {
            onLinkShortcut: () => {},
        }
    },

    addKeyboardShortcuts() {
        return {
            'Mod-Shift-u': () => {
                this.options.onLinkShortcut()
                return true
            },
        }
    },
})

// Ordered list icon (not in @posthog/icons)
function IconOrderedList(): JSX.Element {
    return (
        <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M10 6h10M10 12h10M10 18h10M4 6h1v4M4 10h2M4 15.5a1.5 1.5 0 1 1 3 0c0 .7-.5 1.2-1 1.7L4 19h3"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    )
}

// Underline icon (not in @posthog/icons)
function IconUnderline(): JSX.Element {
    return (
        <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M6 21h12M12 17a5 5 0 0 0 5-5V3M7 3v9a5 5 0 0 0 5 5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    )
}

export type SupportEditorProps = {
    initialContent?: JSONContent | null
    placeholder?: string
    onCreate?: (editor: RichContentEditorType) => void
    onUpdate?: (isEmpty: boolean) => void
    onPressCmdEnter?: () => void
    /** Called when upload state changes (true = uploading, false = idle) */
    onUploadingChange?: (uploading: boolean) => void
    disabled?: boolean
    minRows?: number
    className?: string
}

const DEFAULT_INITIAL_CONTENT: JSONContent = {
    type: 'doc',
    content: [
        {
            type: 'paragraph',
            content: [],
        },
    ],
}

const ImageExtension = Image.configure({
    HTMLAttributes: {
        class: 'SupportEditor__image',
    },
    allowBase64: false,
})

const LinkExtension = Link.configure({
    openOnClick: false, // Don't open links when clicking in editor
    HTMLAttributes: {
        class: 'SupportEditor__link',
        rel: 'noopener noreferrer',
        target: '_blank',
    },
})

/** True when the whole string is a single http(s) URL with no whitespace. */
function isSingleHttpUrl(text: string): boolean {
    if (!text || /\s/.test(text)) {
        return false
    }
    try {
        const { protocol } = new URL(text)
        return protocol === 'http:' || protocol === 'https:'
    } catch {
        return false
    }
}

// TipTap's built-in linkOnPaste only wraps the selection when linkify matches the
// clipboard text exactly, which it fails to do for some long URLs, leaving the
// selection overwritten with plain text instead of hyperlinked. Handle the
// "URL pasted over a selection" case ourselves so URL length doesn't matter.
const LinkOnPasteExtension = Extension.create({
    name: 'supportLinkOnPaste',
    priority: 150, // Run before the Link extension's own paste handler
    addProseMirrorPlugins() {
        const linkType = this.editor.schema.marks.link
        return [
            new Plugin({
                key: new PluginKey('supportLinkOnPaste'),
                props: {
                    handlePaste: (view, event) => {
                        const { selection } = view.state
                        // Only wrap a non-empty text selection. Node selections (e.g. an image)
                        // can't hold inline marks, so let those pastes fall through untouched.
                        if (!(selection instanceof TextSelection) || selection.empty || !linkType) {
                            return false
                        }
                        const text = event.clipboardData?.getData('text/plain').trim()
                        if (!text || !isSingleHttpUrl(text)) {
                            return false
                        }
                        view.dispatch(
                            view.state.tr.addMark(selection.from, selection.to, linkType.create({ href: text }))
                        )
                        return true
                    },
                },
            }),
        ]
    },
})

export const SUPPORT_EXTENSIONS = [
    MentionsExtension,
    EmojiSuggestionExtension,
    RichContentNodeMention,
    ExtensionDocument,
    StarterKit.configure({
        document: false,
        link: false, // We use our own Link extension
        heading: false,
        blockquote: false,
        // bold: enabled - Cmd+B
        // bulletList: enabled - Cmd+Shift+8
        // code: enabled - inline code (Cmd+E) - just visual styling, not executable
        codeBlock: false, // We use our own SupportCodeBlockExtension
        // hardBreak: enabled - allows Shift+Enter for line breaks within paragraphs
        // dropcursor: enabled - shows visual indicator when dragging content
        // gapcursor: enabled - helps position cursor near images/blocks
        horizontalRule: false,
        // italic: enabled - Cmd+I
        // listItem/listKeymap: enabled - required by bulletList/orderedList
        // orderedList: enabled - Cmd+Shift+7
        strike: false,
        underline: false, // Registered explicitly below
    }),
    Underline, // Cmd+U
    ImageExtension,
    LinkExtension,
    LinkOnPasteExtension,
    SupportCodeBlockExtension,
]

export const SUPPORT_PREVIEW_EXTENSIONS = [
    MentionsExtension,
    RichContentNodeMention,
    ExtensionDocument,
    StarterKit.configure({
        document: false,
        link: false, // We use our own Link extension
        codeBlock: false, // We use our own SupportCodeBlockExtension
        underline: false, // Registered explicitly below
    }),
    Underline,
    ImageExtension,
    LinkExtension,
    SupportCodeBlockExtension,
]

// Plain text serialization options for generateText() - used by Comment.tsx
// Extracts plain text with special handling for mentions and images
export const serializationOptions: { textSerializers?: Record<string, TextSerializer> } = {
    textSerializers: {
        [RichContentNodeType.Mention]: ({ node }) => `@member:${node.attrs.id}`,
        image: ({ node }) => `![${node.attrs.alt || 'image'}](${node.attrs.src})`,
        hardBreak: () => '\n',
    },
}

/**
 * Escape special markdown characters in plain text to prevent unintended formatting.
 * Characters: \ ` * _ { } [ ] ( ) # + - . ! |
 */
function escapeMarkdown(text: string): string {
    return text.replace(/([\\`*_{}[\]()#+\-.!|])/g, '\\$1')
}

/**
 * Escape characters that would break image alt text syntax (specifically ])
 */
function escapeAltText(text: string): string {
    return text.replace(/([\\\]])/g, '\\$1')
}

/**
 * Serialize tiptap JSON to markdown string.
 * Handles: bold, italic, code, images, mentions, hard breaks, bullet/ordered lists
 * Note: underline has no standard markdown syntax and is stripped
 */
export function serializeToMarkdown(content: JSONContent): string {
    return serializeNode(content).trim()
}

function serializeNode(node: JSONContent): string {
    if (!node) {
        return ''
    }

    // Handle text nodes with marks
    if (node.type === 'text') {
        let text = node.text || ''
        const marks = node.marks || []
        const hasCodeMark = marks.some((m) => m.type === 'code')

        // Don't escape text inside code marks (it's meant to be literal)
        // For other text, escape markdown special characters
        if (!hasCodeMark) {
            text = escapeMarkdown(text)
        }

        // Find link mark if present (needs special handling)
        const linkMark = marks.find((m) => m.type === 'link')

        for (const mark of marks) {
            switch (mark.type) {
                case 'bold':
                    text = `**${text}**`
                    break
                case 'italic':
                    text = `*${text}*`
                    break
                case 'code':
                    // For code, escape backticks by using more backticks
                    if (text.includes('`')) {
                        text = `\`\` ${text} \`\``
                    } else {
                        text = `\`${text}\``
                    }
                    break
                case 'link':
                    // Link is handled after other marks
                    break
                // underline has no markdown equivalent, skip
            }
        }

        // Apply link mark last (wraps the formatted text)
        if (linkMark && linkMark.attrs?.href) {
            const href = linkMark.attrs.href
            text = `[${text}](${href})`
        }

        return text
    }

    // Handle specific node types
    switch (node.type) {
        case 'doc':
            return (node.content || []).map(serializeNode).join('')

        case 'paragraph': {
            const content = (node.content || []).map(serializeNode).join('')
            return content + '\n\n'
        }

        case 'bulletList':
            return serializeListNode(node, false, '') + '\n\n'

        case 'orderedList':
            return serializeListNode(node, true, '') + '\n\n'

        case 'hardBreak':
            // Two spaces + newline is the standard markdown hard break
            return '  \n'

        case 'image': {
            const src = node.attrs?.src
            // Skip images with empty/missing URLs to avoid broken markdown
            if (!src) {
                return ''
            }
            const alt = escapeAltText(node.attrs?.alt || 'image')
            return `![${alt}](${src})`
        }

        case RichContentNodeType.Mention:
            return `@member:${node.attrs?.id}`

        case 'codeBlock': {
            const language = node.attrs?.language || ''
            const code = (node.content || []).map((n) => n.text || '').join('')
            return `\`\`\`${language}\n${code}\n\`\`\`\n\n`
        }

        default:
            // For unknown nodes, try to serialize children
            if (node.content) {
                return (node.content || []).map(serializeNode).join('')
            }
            return ''
    }
}

function serializeListNode(node: JSONContent, ordered: boolean, indent: string): string {
    const rawStart = Number(node.attrs?.start ?? 1)
    const start = ordered && Number.isFinite(rawStart) ? Math.max(Math.trunc(rawStart), 1) : 1
    const lines: string[] = []
    let position = 0
    for (const item of node.content || []) {
        if (item.type !== 'listItem') {
            continue
        }
        const marker = ordered ? `${start + position}. ` : '- '
        position += 1
        const childIndent = indent + ' '.repeat(marker.length)
        const blocks: { text: string; isNestedList: boolean }[] = []
        for (const child of item.content || []) {
            if (child.type === 'bulletList' || child.type === 'orderedList') {
                const nested = serializeListNode(child, child.type === 'orderedList', childIndent)
                if (nested) {
                    blocks.push({ text: nested, isNestedList: true })
                }
            } else {
                const serialized = serializeNode(child).trim()
                if (serialized) {
                    blocks.push({ text: serialized, isNestedList: false })
                }
            }
        }
        if (blocks.length === 0) {
            lines.push((indent + marker).trimEnd())
            continue
        }
        const [first, ...rest] = blocks
        if (first.isNestedList) {
            lines.push((indent + marker).trimEnd(), first.text)
        } else {
            lines.push(indent + marker + first.text.split('\n').join(`\n${childIndent}`))
        }
        for (const block of rest) {
            if (block.isNestedList) {
                lines.push(block.text)
            } else {
                lines.push(
                    block.text
                        .split('\n')
                        .map((line) => childIndent + line)
                        .join('\n')
                )
            }
        }
    }
    return lines.join('\n')
}

export function SupportEditor({
    initialContent,
    placeholder,
    onCreate,
    onUpdate,
    onPressCmdEnter,
    onUploadingChange,
    disabled = false,
    minRows,
    className,
}: SupportEditorProps): JSX.Element {
    const [isDragging, setIsDragging] = useState<boolean>(false)
    const [ttEditor, setTTEditor] = useState<TTEditor | null>(null)
    // Force re-render when selection changes so toolbar buttons update their active state
    const [, setEditorState] = useState(0)
    const [linkPopoverOpen, setLinkPopoverOpen] = useState(false)
    const [linkUrl, setLinkUrl] = useState('')
    const { objectStorageAvailable } = useValues(preflightLogic)
    const { emojiUsed } = useActions(emojiUsageLogic)

    const openLinkPopover = useCallback(() => {
        const existingHref = ttEditor?.getAttributes('link').href
        setLinkUrl(existingHref || '')
        setLinkPopoverOpen(true)
    }, [ttEditor])

    // Use ref to hold the link shortcut callback so it can access latest state
    const linkShortcutCallbackRef = useRef<() => void>(() => {})
    const handleLinkShortcut = useCallback(() => {
        linkShortcutCallbackRef.current()
    }, [])

    const editor = useRichContentEditor({
        extensions: [
            ...SUPPORT_EXTENSIONS,
            Placeholder.configure({ placeholder }),
            CommandEnterExtension.configure({ onPressCmdEnter }),
            LinkShortcutExtension.configure({ onLinkShortcut: handleLinkShortcut }),
        ],
        disabled,
        initialContent: initialContent ?? DEFAULT_INITIAL_CONTENT,
        onCreate: (editor) => {
            if (onCreate) {
                onCreate(createEditor(editor))
            }
            setTTEditor(editor)
        },
        onUpdate: () => {
            if (onUpdate && ttEditor) {
                onUpdate(ttEditor.isEmpty)
            }
            setEditorState((n) => n + 1)
        },
        onSelectionUpdate: () => {
            setEditorState((n) => n + 1)
        },
    })

    const dropRef = useRef<HTMLDivElement>(null)

    const { setFilesToUpload, filesToUpload, uploading } = useUploadFiles({
        onUpload: (url, fileName) => {
            if (ttEditor) {
                ttEditor.chain().focus().setImage({ src: url, alt: fileName }).run()
            }
            posthog.capture('rich text image uploaded', { name: fileName })
        },
        onError: (detail) => {
            posthog.capture('rich text image upload failed', { error: detail })
            lemonToast.error(`Error uploading image: ${detail}`)
        },
    })

    // Update the link shortcut callback ref when ttEditor changes
    useEffect(() => {
        linkShortcutCallbackRef.current = openLinkPopover
    }, [openLinkPopover])

    // Notify parent of upload state changes
    useEffect(() => {
        onUploadingChange?.(uploading)
    }, [uploading, onUploadingChange])

    const handleDragEnter = (e: React.DragEvent): void => {
        e.preventDefault()
        if (e.dataTransfer.types.includes('Files')) {
            setIsDragging(true)
        }
    }

    const handleDragLeave = (e: React.DragEvent): void => {
        e.preventDefault()
        // Only set to false if we're leaving the container (not entering a child)
        if (e.currentTarget === e.target) {
            setIsDragging(false)
        }
    }

    const handleDragOver = (e: React.DragEvent): void => {
        e.preventDefault()
    }

    const handleDrop = (): void => {
        setIsDragging(false)
    }

    const handlePaste = useCallback(
        (e: ClipboardEvent): void => {
            if (!objectStorageAvailable || !e.clipboardData) {
                return
            }
            const imageItem = Array.from(e.clipboardData.items).find((item) => item.type.startsWith('image/'))
            if (imageItem) {
                const imageFile = imageItem.getAsFile()
                if (imageFile) {
                    e.preventDefault()
                    setFilesToUpload([imageFile])
                }
            }
        },
        [objectStorageAvailable, setFilesToUpload]
    )

    useEffect(() => {
        const el = dropRef.current
        if (!el) {
            return
        }
        el.addEventListener('paste', handlePaste)
        return () => el.removeEventListener('paste', handlePaste)
    }, [handlePaste])

    return (
        <div
            ref={dropRef}
            className={cn(
                'SupportEditor flex flex-col border rounded divide-y mt-4 input-like transition-shadow',
                isDragging && 'ring-2 ring-primary ring-offset-1',
                className
            )}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            <EditorContent
                editor={editor}
                className="SupportEditor__content p-2"
                autoFocus
                style={minRows ? { minHeight: `${minRows * 1.5}em` } : undefined}
            />
            <div className="flex justify-between p-0.5">
                <div className="flex items-center">
                    <LemonButton
                        size="small"
                        active={ttEditor?.isActive('bold')}
                        onClick={() => ttEditor?.chain().focus().toggleBold().run()}
                        icon={<IconBold />}
                        tooltip="Bold (Cmd+B)"
                    />
                    <LemonButton
                        size="small"
                        active={ttEditor?.isActive('italic')}
                        onClick={() => ttEditor?.chain().focus().toggleItalic().run()}
                        icon={<IconItalic />}
                        tooltip="Italic (Cmd+I)"
                    />
                    <LemonButton
                        size="small"
                        active={ttEditor?.isActive('underline')}
                        onClick={() => ttEditor?.chain().focus().toggleUnderline().run()}
                        icon={<IconUnderline />}
                        tooltip="Underline (Cmd+U)"
                    />
                    <LemonButton
                        size="small"
                        active={ttEditor?.isActive('bulletList')}
                        onClick={() => ttEditor?.chain().focus().toggleBulletList().run()}
                        icon={<IconList />}
                        tooltip="Bullet list (Cmd+Shift+8)"
                    />
                    <LemonButton
                        size="small"
                        active={ttEditor?.isActive('orderedList')}
                        onClick={() => ttEditor?.chain().focus().toggleOrderedList().run()}
                        icon={<IconOrderedList />}
                        tooltip="Numbered list (Cmd+Shift+7)"
                    />
                    <LemonButton
                        size="small"
                        active={ttEditor?.isActive('code')}
                        onClick={() => ttEditor?.chain().focus().toggleCode().run()}
                        icon={<IconCode />}
                        tooltip="Inline code (Cmd+E)"
                    />
                    <LemonButton
                        size="small"
                        active={ttEditor?.isActive('codeBlock')}
                        onClick={() => ttEditor?.chain().focus().toggleCodeBlock().run()}
                        icon={<IconTerminal />}
                        tooltip="Code block (Cmd+Alt+C)"
                    />
                    <Popover
                        visible={linkPopoverOpen}
                        onClickOutside={() => {
                            setLinkPopoverOpen(false)
                            setLinkUrl('')
                        }}
                        overlay={
                            <div className="p-2 flex gap-2 items-center">
                                <LemonInput
                                    size="small"
                                    placeholder="https://..."
                                    value={linkUrl}
                                    onChange={setLinkUrl}
                                    autoFocus
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && linkUrl) {
                                            e.preventDefault()
                                            if (ttEditor) {
                                                ttEditor.chain().focus().setLink({ href: linkUrl }).run()
                                            }
                                            setLinkPopoverOpen(false)
                                            setLinkUrl('')
                                        } else if (e.key === 'Escape') {
                                            setLinkPopoverOpen(false)
                                            setLinkUrl('')
                                        }
                                    }}
                                />
                                <LemonButton
                                    size="small"
                                    type="primary"
                                    onClick={() => {
                                        if (ttEditor && linkUrl) {
                                            ttEditor.chain().focus().setLink({ href: linkUrl }).run()
                                        }
                                        setLinkPopoverOpen(false)
                                        setLinkUrl('')
                                    }}
                                    disabledReason={!linkUrl ? 'Enter a URL' : undefined}
                                >
                                    Add
                                </LemonButton>
                                {ttEditor?.isActive('link') && (
                                    <LemonButton
                                        size="small"
                                        type="secondary"
                                        onClick={() => {
                                            ttEditor?.chain().focus().unsetLink().run()
                                            setLinkPopoverOpen(false)
                                            setLinkUrl('')
                                        }}
                                    >
                                        Remove
                                    </LemonButton>
                                )}
                            </div>
                        }
                    >
                        <LemonButton
                            size="small"
                            active={ttEditor?.isActive('link')}
                            onClick={openLinkPopover}
                            icon={<IconLink />}
                            tooltip="Add link (Cmd+Shift+U)"
                        />
                    </Popover>
                    <div className="w-px h-4 bg-border mx-1" />
                    <LemonFileInput
                        key="file-upload"
                        accept={'image/*'}
                        multiple={false}
                        alternativeDropTargetRef={dropRef}
                        onChange={setFilesToUpload}
                        loading={uploading}
                        value={filesToUpload}
                        showUploadedFiles={false}
                        callToAction={
                            <LemonButton
                                size="small"
                                icon={
                                    uploading ? (
                                        <Spinner className="text-lg" textColored={true} />
                                    ) : (
                                        <IconImage className="text-lg" />
                                    )
                                }
                                disabledReason={
                                    objectStorageAvailable
                                        ? undefined
                                        : 'Enable object storage to add images by dragging and dropping'
                                }
                                tooltip={objectStorageAvailable ? 'Click here or drag and drop to upload images' : null}
                            />
                        }
                    />
                    <EmojiPickerPopover
                        key="emoj-picker"
                        data-attr="lemon-rich-text-editor-emoji-popover"
                        onSelect={(emoji: string) => {
                            if (ttEditor) {
                                ttEditor.commands.insertContent(emoji)
                                emojiUsed(emoji)
                            }
                        }}
                    />
                </div>
            </div>
        </div>
    )
}
